import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Search } from "lucide-react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { api, fileUrl } from "@/lib/api";

export default function Societies() {
    const [socs, setSocs] = useState([]);
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/societies", { params: q ? { q } : {} }).then((r) => setSocs(r.data));
    }, [q]);

    return (
        <PublicLayout>
            <section className="container-x pt-10">
                <Breadcrumbs items={[{ label: "Our Societies" }]} />
                <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <div className="overline">Member Societies</div>
                        <h1 className="mt-3 font-serif text-5xl text-slate-900 sm:text-6xl">Our Societies</h1>
                        <p className="mt-4 max-w-2xl text-lg text-slate-600">
                            Every member society brings its own story, its own people and its own traditions. Together we make one community.
                        </p>
                    </div>
                    <div className="relative w-full max-w-xs">
                        <Search size={16} className="absolute left-3 top-3 text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search societies…"
                            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                            data-testid="societies-search-input"
                        />
                    </div>
                </div>
            </section>

            <section className="container-x mt-10 pb-20">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {socs.map((s) => (
                        <Link key={s.id} to={`/societies/${s.slug}`} className="surface-card-hover overflow-hidden" data-testid={`society-card-${s.slug}`}>
                            <div className="relative h-44 bg-gradient-to-br from-brand-blue via-brand-blue-dark to-slate-900">
                                {s.cover_id ? (
                                    <img src={fileUrl(s.cover_id)} alt={s.name} className="h-full w-full object-cover" loading="lazy" />
                                ) : (
                                    <div className="grid h-full place-items-center text-white/40"><Building2 size={56} /></div>
                                )}
                                {s.featured && (
                                    <span className="absolute left-4 top-4 rounded-full bg-brand-orange px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">Featured</span>
                                )}
                            </div>
                            <div className="p-6">
                                <div className="font-serif text-xl text-slate-900">{s.name}</div>
                                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                                    <span>Reg: {s.registration_number || "—"}</span>
                                    {s.registration_date && <span>Since {new Date(s.registration_date).getFullYear()}</span>}
                                </div>
                                <p className="mt-3 line-clamp-3 text-sm text-slate-600">{s.short_description}</p>
                                <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                                    <span>{s.flats} flats · {s.buildings} bldg</span>
                                    <span className="font-medium text-brand-blue">View details →</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                    {socs.length === 0 && (
                        <div className="col-span-full py-16 text-center text-slate-500">No societies match your search.</div>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
